import React from 'react';
import {
  AllieTableBodyProps,
  AllieTableCaptionProps,
  AllieTableCellProps,
  AllieTableComponent,
  AllieTableDensity,
  AllieTableEmptyProps,
  AllieTableFooterProps,
  AllieTableHeaderProps,
  AllieTableHeadProps,
  AllieTableInteraction,
  AllieTableProps,
  AllieTableProgressProps,
  AllieTableRowProps,
  AllieTableRowStyle,
  AllieTableStatusBadgeProps,
} from './AllieTable.types';
import {
  StyledTable,
  StyledTableBody,
  StyledTableCaption,
  StyledTableCell,
  StyledTableContainer,
  StyledTableEmptyCell,
  StyledTableFooter,
  StyledTableHeadCell,
  StyledTableHeader,
  StyledTableRow,
  StyledTableScroll,
  TableProgress,
  TableProgressFill,
  TableProgressLabel,
  TableProgressTrack,
  TableStatusBadge,
} from './AllieTable.styles';

interface AllieTableContextValue {
  density: AllieTableDensity;
  rowStyle: AllieTableRowStyle;
  interaction: AllieTableInteraction;
}

const TABLE_CONTEXT_DEFAULTS: AllieTableContextValue = {
  density: 'default',
  rowStyle: 'plain',
  interaction: 'none',
};

const AllieTableContext = React.createContext<AllieTableContextValue>(TABLE_CONTEXT_DEFAULTS);

const useAllieTableContext = () => React.useContext(AllieTableContext);

export const AllieTable: AllieTableComponent = ({
  children,
  variant = 'plain',
  density = 'default',
  layout = 'auto',
  rowStyle = 'plain',
  interaction = 'none',
  minWidth,
  wrapperClassName,
  ...props
}: AllieTableProps) => {
  const contextValue = React.useMemo(
    () => ({
      density,
      rowStyle,
      interaction,
    }),
    [density, interaction, rowStyle]
  );

  return (
    <AllieTableContext.Provider value={contextValue}>
      <StyledTableContainer className={wrapperClassName} $variant={variant}>
        <StyledTableScroll>
          <StyledTable $layout={layout} $minWidth={minWidth} {...props}>
            {children}
          </StyledTable>
        </StyledTableScroll>
      </StyledTableContainer>
    </AllieTableContext.Provider>
  );
};

const TableCaption = ({ children, ...props }: AllieTableCaptionProps) => (
  <StyledTableCaption {...props}>{children}</StyledTableCaption>
);

const TableHeader = ({ children, ...props }: AllieTableHeaderProps) => (
  <StyledTableHeader {...props}>{children}</StyledTableHeader>
);

const TableBody = ({ children, ...props }: AllieTableBodyProps) => (
  <StyledTableBody {...props}>{children}</StyledTableBody>
);

const TableFooter = ({ children, ...props }: AllieTableFooterProps) => (
  <StyledTableFooter {...props}>{children}</StyledTableFooter>
);

const TableRow = ({ children, ...props }: AllieTableRowProps) => {
  const { rowStyle, interaction } = useAllieTableContext();

  return (
    <StyledTableRow $rowStyle={rowStyle} $interaction={interaction} {...props}>
      {children}
    </StyledTableRow>
  );
};

const TABLE_HEAD_DEFAULT_CLASS = 'allie-text-neutral-50';

const TableHead = ({
  children,
  align = 'left',
  width,
  className,
  ...props
}: AllieTableHeadProps) => {
  const { density } = useAllieTableContext();

  return (
    <StyledTableHeadCell
      $align={align}
      $density={density}
      $width={width}
      className={className ?? TABLE_HEAD_DEFAULT_CLASS}
      {...props}
    >
      {children}
    </StyledTableHeadCell>
  );
};

const TableCell = ({
  children,
  align = 'left',
  tone = 'default',
  width,
  ...props
}: AllieTableCellProps) => {
  const { density } = useAllieTableContext();

  return (
    <StyledTableCell
      $align={align}
      $density={density}
      $tone={tone}
      $width={width}
      {...props}
    >
      {children}
    </StyledTableCell>
  );
};

const TableEmpty = ({ children, colSpan, ...props }: AllieTableEmptyProps) => {
  const { density } = useAllieTableContext();

  return (
    <TableRow>
      <StyledTableEmptyCell
        $align="center"
        $density={density}
        $tone="muted"
        colSpan={colSpan}
        {...props}
      >
        {children}
      </StyledTableEmptyCell>
    </TableRow>
  );
};

const TableStatusBadgeCell = ({ status, children }: AllieTableStatusBadgeProps) => (
  <TableStatusBadge $status={status}>{children}</TableStatusBadge>
);

const TableProgressCell = ({
  value,
  status = 'ok',
  label,
}: AllieTableProgressProps) => {
  const pct = Math.min(Math.max(value, 0), 100);

  return (
    <TableProgress
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={label}
    >
      <TableProgressLabel>{label}</TableProgressLabel>
      <TableProgressTrack>
        <TableProgressFill $pct={pct} $status={status} />
      </TableProgressTrack>
    </TableProgress>
  );
};

AllieTable.Caption = TableCaption;
AllieTable.Header = TableHeader;
AllieTable.Body = TableBody;
AllieTable.Footer = TableFooter;
AllieTable.Row = TableRow;
AllieTable.Head = TableHead;
AllieTable.Cell = TableCell;
AllieTable.Empty = TableEmpty;
AllieTable.StatusBadge = TableStatusBadgeCell;
AllieTable.Progress = TableProgressCell;

export default AllieTable;
